import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { CHART_TONES, ChartTableFallback, ChartTooltip, SvgFrame, defaultFormat, useChartTooltip, type ChartTone } from "./chart-base";

const R = 15.915;
const C = 21;
const SEGMENT_GAP = 0.6;

export interface DonutDatum {
  label: string;
  value: number;
  tone: ChartTone;
}

export interface DonutChartProps {
  title: string;
  description?: string;
  data: readonly DonutDatum[];
  /** Big number in the centre (defaults to the formatted total). */
  centerValue?: ReactNode;
  centerLabel?: ReactNode;
  formatValue?: (value: number) => string;
  className?: string;
}

const percentText = (value: number, total: number) => `${total > 0 ? Math.round((value / total) * 100) : 0}%`;

/** Channel split donut (pathLength=100 dash segments) + legend with label, percent and count. */
export function DonutChart({ title, description, data, centerValue, centerLabel, formatValue = defaultFormat, className }: DonutChartProps) {
  const { tip, show, hide } = useChartTooltip();
  const total = data.reduce((sum, d) => sum + Math.max(d.value, 0), 0);
  const shares = data.map((d) => (total > 0 ? (Math.max(d.value, 0) / total) * 100 : 0));
  const offsets = shares.map((_, i) => shares.slice(0, i).reduce((sum, share) => sum + share, 0));
  return (
    <div className={cn("flex flex-col items-center gap-5 sm:flex-row", className)}>
      <div className="relative size-40 shrink-0">
        <SvgFrame title={title} description={description} viewBox="0 0 42 42" className="size-40" onMouseLeave={hide}>
          <g transform={`rotate(-90 ${C} ${C})`}>
            <circle cx={C} cy={C} r={R} fill="none" strokeWidth={6} className="stroke-sunken" />
            {data.map((d, i) => {
              const len = shares[i] ?? 0;
              const text = `${d.label}: ${formatValue(d.value)} (${percentText(d.value, total)})`;
              const reveal = () => {
                show({ x: 50, y: 8, text, index: i });
              };
              return (
                <circle
                  key={d.label}
                  cx={C}
                  cy={C}
                  r={R}
                  fill="none"
                  strokeWidth={6}
                  pathLength={100}
                  strokeDasharray={`${Math.max(len - SEGMENT_GAP, 0)} ${100 - len + SEGMENT_GAP}`}
                  strokeDashoffset={-(offsets[i] ?? 0)}
                  tabIndex={0}
                  role="img"
                  aria-label={text}
                  className={cn(CHART_TONES[d.tone].stroke, "outline-none hover:opacity-80 focus-visible:[stroke-width:8]")}
                  onMouseEnter={reveal}
                  onFocus={reveal}
                  onBlur={hide}
                />
              );
            })}
          </g>
        </SvgFrame>
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xl font-extrabold tabular-nums text-fg">{centerValue ?? formatValue(total)}</div>
          {centerLabel ? <div className="text-[11px] text-fg-subtle">{centerLabel}</div> : null}
        </div>
        <ChartTooltip tip={tip} />
      </div>
      <ul className="w-full space-y-2 text-sm">
        {data.map((d) => (
          <li key={d.label} className="flex items-center gap-2">
            <span aria-hidden="true" className={cn("size-2.5 rounded-xs", CHART_TONES[d.tone].swatch)} />
            <span className="text-fg">{d.label}</span>
            <span className="ml-auto font-semibold tabular-nums text-fg">{percentText(d.value, total)}</span>
            <span className="w-12 text-right text-xs tabular-nums text-fg-subtle">{formatValue(d.value)}</span>
          </li>
        ))}
      </ul>
      <ChartTableFallback
        caption={title}
        table={{ columns: ["", "Count", "Share"], rows: data.map((d) => [d.label, formatValue(d.value), percentText(d.value, total)]) }}
      />
    </div>
  );
}
